import { type ReactNode, useEffect, useMemo, useState } from "react";
import type { ReportDates } from "@/profitability/types";
import { SourceContext, type ShowSource } from "./source-context";
import { SourceDialog } from "./source-dialog";
import { canShowSource } from "./sourced";
import type { SourceTarget } from "./types";

/**
 * Offers "Show source" to every figure on the page and holds its dialog: a
 * record's figures beside the file they were read from. The page's period
 * run is where citations are read, unless a record's row is from another.
 * "Open in Documents" goes to this template's Documents tab on that period;
 * a figure nothing matched offers its file's upload for that period.
 */
export const SourceProvider = ({
  businessId,
  reportId,
  runId,
  compare = null,
  dates = null,
  canUpload = false,
  children,
}: {
  businessId: string;
  reportId: string;
  /** The period on screen; null offers nothing. */
  runId: string | null;
  /** The page's comparison, kept on the link to the Documents tab. */
  compare?: string | null;
  /** The period's dates, where a figure missing from it is uploaded. */
  dates?: ReportDates | null;
  /** Whether the person may upload files for a period (`canManageReport`). */
  canUpload?: boolean;
  children: ReactNode;
}) => {
  const [target, setTarget] = useState<SourceTarget | null>(null);
  // Each opening mounts its own dialog, so it starts on its own figure.
  const [opening, setOpening] = useState(0);
  // Held apart from the target, so the dialog animates out with its content.
  const [open, setOpen] = useState(false);
  const [container, setContainer] = useState<HTMLDialogElement | null>(null);
  // A resize can replace the modal record with a docked panel.
  useEffect(() => {
    if (!open) return;
    const syncContainer = () => {
      const modal =
        Array.from(
          document.querySelectorAll<HTMLDialogElement>("dialog:modal"),
        ).at(-1) ?? null;
      if (modal !== container) setContainer(modal);
    };
    syncContainer();
    const observer = new MutationObserver(syncContainer);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["open"],
    });
    return () => observer.disconnect();
  }, [open, container]);
  const value = useMemo<ShowSource>(
    () => ({
      open: (next) => {
        setContainer(
          document.activeElement?.closest<HTMLDialogElement>("dialog:modal") ??
            null,
        );
        setTarget(next);
        setOpening((count) => count + 1);
        setOpen(true);
      },
      available: (dataset, column) => !!runId && canShowSource(dataset, column),
      isOpen: open,
    }),
    [runId, open],
  );
  const shownRun = target?.runId ?? runId;
  return (
    <SourceContext.Provider value={value}>
      {children}
      {target && shownRun && (
        <SourceDialog
          key={opening}
          businessId={businessId}
          reportId={reportId}
          runId={shownRun}
          compare={compare}
          target={target}
          upload={
            canUpload
              ? { dates: target.runId ? (target.dates ?? null) : dates }
              : null
          }
          open={open}
          container={container}
          onClose={() => setOpen(false)}
          onClosed={() => setTarget(null)}
        />
      )}
    </SourceContext.Provider>
  );
};
